/**
 * Pure formatting/derivation helpers for experiment views.
 *
 * Deliberately free of React and Cloudscape imports so this logic is unit
 * testable without a DOM. Presentation lives in experimentShared.tsx.
 */
import { ExperimentCounts, ExperimentFilters, ExperimentRun, ExperimentStatus } from '../../types/experiment';

export const POLL_INTERVAL_MS = 3000;

export const STAGE_LABELS: Record<string, string> = {
  queued: 'Queued',
  starting: 'Starting',
  summarizing: 'Summarizing tickets',
  done: 'Done',
  failed: 'Failed',
  cancelled: 'Cancelled',
};

/**
 * In-progress statuses. CANCELLING counts as active: the worker has not yet
 * acknowledged the cancel, so callers must keep polling until it settles —
 * otherwise the UI would stop updating on a run that is still finishing.
 */
export function isActive(status: ExperimentStatus): boolean {
  return status === 'PENDING' || status === 'RUNNING' || status === 'CANCELLING';
}

export function progressText(run: Pick<ExperimentRun, 'progress'>): string {
  const stage = run.progress?.stage ?? 'queued';
  const label = STAGE_LABELS[stage] ?? stage;
  const p = (run.progress ?? {}) as Record<string, number | undefined>;
  if (stage === 'summarizing' && p.total != null) {
    return `${label} (${p.done ?? 0} of ${p.total})`;
  }
  return label;
}

export function scopeText(filters?: ExperimentFilters): string {
  if (!filters?.dateFrom && !filters?.dateTo) return 'All time';
  return `${filters?.dateFrom || '—'} to ${filters?.dateTo || '—'}`;
}

export function groupsText(filters?: ExperimentFilters): string {
  return filters?.assignedGroups?.length ? filters.assignedGroups.join(', ') : 'All groups';
}

export function severitiesText(filters?: ExperimentFilters): string {
  return filters?.severities?.length
    ? filters.severities.map(severityLabel).join(', ')
    : 'All';
}

/** "July 24, 2026, 04:08 (UTC)" — matches the themes views. */
export function absoluteTime(iso?: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  const formatted = d.toLocaleString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: 'UTC',
  });
  return `${formatted} (UTC)`;
}

/**
 * Summarize a run's outcome. Failed and skipped counts are only shown when
 * non-zero, so a clean run reads as a plain success rather than a list of zeros.
 */
export function countsText(counts?: ExperimentCounts): string {
  if (!counts || counts.total == null) return '—';
  const parts = [`${counts.ok ?? 0} summarized`];
  if (counts.failed) parts.push(`${counts.failed} failed`);
  if (counts.skipped) parts.push(`${counts.skipped} skipped`);
  return parts.join(', ');
}

/** Severities present in the corpus. 2.5 is a real Amazon severity ("daytime Sev-2"). */
export const ALL_SEVERITY_OPTIONS = [
  { label: 'SEV 1', value: 'SEV_1' },
  { label: 'SEV 2', value: 'SEV_2' },
  { label: 'SEV 2.5', value: 'SEV_2.5' },
  { label: 'SEV 3', value: 'SEV_3' },
  { label: 'SEV 4', value: 'SEV_4' },
  { label: 'SEV 5', value: 'SEV_5' },
];

/** "SEV_2.5" -> "SEV 2.5". Matches the label style used across the reports. */
export function severityLabel(value: string): string {
  return value.replace('SEV_', 'SEV ');
}

/**
 * Severity options for a scope selector, taken from the configured ingestion
 * filters so the list cannot offer a severity the corpus does not contain —
 * selecting one would scope a run to zero tickets. Falls back to the full list
 * when configuration is unavailable, since an empty dropdown is worse than a
 * slightly over-generous one.
 */
export function toSeverityOptions(filters: unknown): { label: string; value: string }[] {
  const raw = (filters as { severities?: unknown } | undefined)?.severities;
  if (!Array.isArray(raw)) return ALL_SEVERITY_OPTIONS;
  const options = raw
    .filter((s): s is string => typeof s === 'string' && s.trim() !== '')
    .map((s) => ({ label: severityLabel(s.trim()), value: s.trim() }));
  return options.length ? options : ALL_SEVERITY_OPTIONS;
}

/**
 * Validate the scope date range. An open-ended range is allowed — "since
 * January" and "up to March" are both legitimate scopes the backend supports —
 * but an inverted one is always a mistake, and left unchecked it produced a run
 * with total: 0 that read as a broken experiment rather than an empty scope.
 */
export function dateRangeError(dateFrom: string, dateTo: string): string {
  const from = dateFrom.trim();
  const to = dateTo.trim();
  if (!from || !to) return '';
  // ISO yyyy-mm-dd sorts lexicographically, which is why no Date parsing is needed.
  return from > to ? 'Start date must be on or before end date.' : '';
}

/**
 * Map the settings filters payload to dropdown options. Degrades to an empty
 * list on anything unexpected: a settings hiccup should cost the operator the
 * dropdown, not the whole launch form.
 */
export function toGroupOptions(filters: unknown): { label: string; value: string }[] {
  const raw = (filters as { assignedGroups?: unknown } | undefined)?.assignedGroups;
  if (!Array.isArray(raw)) return [];
  return raw
    .map((g) => (g as { value?: unknown })?.value)
    .filter((v): v is string => typeof v === 'string' && v.trim() !== '')
    .map((v) => ({ label: v, value: v }));
}

/**
 * Build the run's scope from the form selections, omitting anything not chosen.
 * Empty must mean absent rather than an empty array, because absent is what the
 * backend reads as "all groups" / "all severities".
 */
export function buildExperimentFilters(input: {
  dateFrom: string;
  dateTo: string;
  groups: string[];
  severities: string[];
}): ExperimentFilters {
  const groups = input.groups.filter((g) => g.trim() !== '');
  const filters: ExperimentFilters = {};
  if (input.dateFrom.trim()) filters.dateFrom = input.dateFrom.trim();
  if (input.dateTo.trim()) filters.dateTo = input.dateTo.trim();
  if (groups.length) filters.assignedGroups = groups;
  if (input.severities.length) filters.severities = input.severities;
  return filters;
}

/** Validate the per-run ticket cap client-side; mirrors the server's bounds. */
export const CAP_DEFAULT = 50;
export const CAP_MAX = 300;

export function capError(raw: string): string {
  if (raw === '') return '';
  const n = Number(raw);
  if (!Number.isInteger(n) || n < 1 || n > CAP_MAX) {
    return `Enter a whole number between 1 and ${CAP_MAX}`;
  }
  return '';
}
